import type { FilesApi, UploadProgress } from "./api";

/**
 * Тип файла для начала загрузки.
 *
 * Браузер определяет тип непоследовательно: презентация и архив приходят как
 * application/octet-stream, а файл вроде `nginx.log` — вообще без типа. Тип по
 * расширению имени определяет сервер — он же затем проверяет содержимое, —
 * поэтому отсюда уходит ровно то, что сообщил браузер.
 */
export function uploadMime(file: File): string {
  return file.type === "" ? "application/octet-stream" : file.type;
}

/** Файл, который уже лежит в хранилище и подтверждён сервером. */
export interface ConfirmedUpload {
  readonly id: string;
  readonly filename: string;
  readonly size: number;
}

/**
 * Общий путь загрузки: подписанная ссылка → байты в хранилище → подтверждение.
 *
 * Все места, где к документу приходит новый файл, — вложение к письму или
 * сообщению, новый файл документа, новая версия — идут этим путём. Байты не
 * проходят через workflow-операции: туда передаётся только идентификатор
 * подтверждённого файла.
 */
export async function uploadConfirmedFile(
  files: Pick<FilesApi, "initiate" | "uploadObject" | "confirm">,
  file: File,
  options: {
    /** Контур документа при работе из сводного списка «Все организации». */
    readonly accessScopeId?: string;
    readonly onProgress?: UploadProgress;
  } = {},
): Promise<ConfirmedUpload> {
  const initiated = await files.initiate({
    purpose: "temporary",
    filename: file.name,
    mime_type: uploadMime(file),
    size: file.size,
    ...(options.accessScopeId
      ? { access_scope_id: options.accessScopeId }
      : {}),
  });
  options.onProgress?.(0);
  await files.uploadObject(initiated.upload, file, options.onProgress);
  const confirmed = await files.confirm(initiated.data.id);

  return {
    id: confirmed.data.id,
    filename: confirmed.data.filename,
    size: confirmed.data.size,
  };
}
